// Copyright (c) Microsoft. All rights reserved.

import { useMsal } from '@azure/msal-react';
import { Button, Image, Title3 } from '@fluentui/react-components';
import React from 'react';
import signInLogo from '../assets/ms-symbollockup_signin_light.svg';
import proGPTLogo from '../assets/login-image.png';
import { makeStyles } from '@fluentui/react-components';
import { customColors, customFonts } from '../styles';
import proGPTLoginLogo from '../assets/ProGPT-Logo-Light-Blue.svg';
import { trackException } from './telemetry/appInsights';
import { getCachedAuthEnvironment } from '../services/auth/AuthEnvironmentResolver';
import { loginBrowserAsync } from '../services/auth/BrowserAuthService';
import { loginTeamsAsync } from '../services/auth/TeamsAuthService';
import { alerts } from '../shared/ui/Alerts';

export const useClasses = makeStyles({
    root: {
        display: 'flex',
        height: '100%',
        justifyContent: 'center',
        alignItems: 'center',
        paddingTop: '10%',
        paddingBottom: '20%',
        backgroundColor: customColors.lightBlueGrayBackground1,
        flexShrink: '1',
        minWidth: '650px',
        minHeight: '260px',
        '@media(max-height: 600px)': {
            paddingTop: '20%',
        },
        '@media(max-width: 768px)': {
            paddingTop: '20%',
            minWidth: '300px',
            minHeight: '275px',
            width: '100%',
        },
    },
    container: {
        marginTop: '10px',
        border: '1px solid transparent',
        borderRadius: '8px',
        display: 'flex',
        width: '55vw',
        height: '35vh',
        flexShrink: '1',
        minWidth: '650px',
        minHeight: '260px',
        '@media(max-width: 768px)': {
            minWidth: '300px',
            minHeight: '275px',
            width: '100%',
        },
    },
    loginContainer: {
        minWidth: '60%',
        minHeight: 'auto',
        width: '60%',
        borderRadius: '8px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-evenly',
        alignItems: 'center',
        textAlign: 'center',
        flexWrap: 'wrap',
        flexShrink: '1',
        backgroundColor: 'white',
        '@media(max-width: 768px)': {
            justifyContent: 'space-evenly',
        },
    },
    imageContainer: {
        backgroundColor: customColors.protivitiDarkBlue,
        borderTopLeftRadius: '8px',
        borderBottomLeftRadius: '8px',
        width: '40%',
    },
    title: {
        margin: '8px 0px 8px 0px',
        color: customColors.protivitiDarkBlue,
        font: customFonts.Lato,
        fontWeight: '500',
        fontSize: '1.8rem',
        '@media(max-width: 472px)': {
            fontSize: '1.3rem',
        },
    },
    signInButton: {
        margin: '0px 0px 8px 0px',
        padding: '0',
        '@media(max-width: 768px)': {
            margin: '0',
        },
    },
    image: {
        borderTopLeftRadius: '8px',
        borderBottomLeftRadius: '8px',
    },
});

export const Login: React.FC = () => {
    const { instance } = useMsal();
    const classes = useClasses();

    const handleClick = async () => {
        const env = getCachedAuthEnvironment();

        try {
            if (env?.isInTeams) {
                const teamsAccount = await loginTeamsAsync({ msalInstance: instance });
                if (teamsAccount) {
                    window.location.reload();
                    return;
                }

                throw new Error('Teams sign-in did not produce a usable account.');
            } else if (env?.isSharePoint) {
                await instance.loginPopup();
            } else {
                await loginBrowserAsync({ msalInstance: instance });
            }
        } catch (error) {
            trackException(error, {
                source: 'Login.handleClick',
                environment: env?.isInTeams ? 'Teams' : 'Browser',
            });

            if (env?.isInTeams) {
                alerts.error('Sign-in failed. Please sign out of Teams and sign back in to try again.');
            }
        }
    };

    return (
        <>
            <div className={classes.root} data-testid="login-view">
                <div className={classes.container}>
                    <div className={classes.imageContainer}>
                        <Image className={classes.image} src={proGPTLogo} fit="cover" />
                    </div>
                    <div className={classes.loginContainer}>
                        <Title3 className={classes.title}>Welcome to ProGPT</Title3>
                        <Button className={classes.signInButton} appearance="transparent" onClick={handleClick}>
                            <Image className={classes.signInButton} src={signInLogo} />
                        </Button>
                        <div>
                            <Image fit="contain" src={proGPTLoginLogo} />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};
